import { useState } from "react";
import { ProductExclusionReason } from "@acto/gql/graphql";
import { useTranslations } from "@acto/web-common/locales";
import { useHideProducts } from "@acto/taskpane/core/data-access/product-exclusions";
import { AnalyticsEvents, useTrack, type ProductExclusionCreatedProperties } from "@acto/taskpane/core/analytics";
import { useMeetingPrepProductExclusions } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/MeetingPrepProductExclusionsContext";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import {
  productNamesById,
  validToForDuration,
  type HideableProduct,
  type SettledDuration,
} from "@acto/taskpane/field-sales-2/utils/productHide";
import { AdjustProductsCard, type PickerReturn } from "./AdjustProductsCard";
import { ProductHideOverlay } from "./ProductHideOverlay";
import { SelectionActionBar } from "./SelectionActionBar";
import { useHiddenProductViews } from "./useHiddenProductViews";
import type { ReasonDraft, ReasonKind } from "./ProductHideReasonPicker";

const REASON_BY_KIND: Record<ReasonKind, ProductExclusionReason> = {
  notSuitable: ProductExclusionReason.ProductNotSuitable,
  dataIncorrect: ProductExclusionReason.DataIncorrect,
};

type Step = "select" | "confirm";

type TrackedDuration = Pick<ProductExclusionCreatedProperties, "duration" | "snoozeUntil">;

const trackedDuration = (duration: SettledDuration, validTo: string | null): TrackedDuration => {
  switch (duration.kind) {
    case "once":
      return { duration: "once" };
    case "forever":
      return { duration: "indefinite" };
    case "until":
      return { duration: "snoozed", snoozeUntil: validTo ?? undefined };
  }
};

interface ProductHideFlowProps {
  /** Includes already hidden products: the flow separates them into the restore list itself. */
  products: HideableProduct[];
  talkingPointLabel: string;
  onBack: () => void;
  returnsTo?: PickerReturn;
  clientId: string;
  today?: Date;
}

export function ProductHideFlow({
  products: allProducts,
  talkingPointLabel,
  onBack,
  returnsTo,
  clientId,
  today,
}: ProductHideFlowProps) {
  const { exclusions, restore, restoringIds } = useMeetingPrepProductExclusions();
  const { hideProducts } = useHideProducts();
  const track = useTrack();
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");

  const [step, setStep] = useState<Step>("select");
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const hiddenIds = new Set(exclusions.map((exclusion) => exclusion.productId));
  const names = productNamesById(allProducts);
  const hidden = useHiddenProductViews(exclusions, names);

  const selectable = allProducts.filter((product) => !product.productIds.every((id) => hiddenIds.has(id)));
  const selected = selectable.filter((product) => selectedIds.has(product.id));

  const close = () => {
    setSelectedIds(new Set());
    setStep("select");
    onBack();
  };

  // A confirmation cut short by a pick must not return when the pick is cleared.
  const toggle = (id: string) => {
    setConfirmation(null);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  const confirm = async (reason: ReasonDraft, duration: SettledDuration) => {
    const hiddenNow = selected.length;
    const productIds = selected.flatMap((product) => product.productIds);
    const validTo = validToForDuration(duration, today);

    setIsSubmitting(true);
    const result = await hideProducts({
      clientId,
      products: productIds,
      reason: REASON_BY_KIND[reason.kind],
      reasonText: reason.text,
      validTo,
    });
    setIsSubmitting(false);
    const failed = result === null;
    if (failed) return;

    track(AnalyticsEvents.PRODUCT_EXCLUSION_CREATED, {
      customerId: clientId,
      exclusionType: hiddenNow === selectable.length ? "all_products" : "specific_products",
      productIds,
      ...trackedDuration(duration, validTo),
    });

    setSelectedIds(new Set());
    setStep("select");
    setConfirmation(t("hiddenCount", { count: hiddenNow }));
  };

  const restoreWithConfirmation = async (id: string) => {
    if (!(await restore(id))) return;
    setConfirmation(t("restoredCount", { count: 1 }));
  };

  return (
    <>
      <AdjustProductsCard
        talkingPointLabel={talkingPointLabel}
        selectable={selectable}
        hidden={hidden}
        selectedIds={selectedIds}
        onToggle={toggle}
        onBack={close}
        returnsTo={returnsTo}
        onRestore={(id) => void restoreWithConfirmation(id)}
        restoringIds={restoringIds}
        confirmation={confirmation}
        onConfirmationClose={() => setConfirmation(null)}
      />
      {selected.length > 0 && (
        <ShellSlotContent slot="bottomNavTabRow" id="productHideSelection">
          <SelectionActionBar
            count={selected.length}
            onClear={() => setSelectedIds(new Set())}
            onContinue={() => setStep("confirm")}
          />
        </ShellSlotContent>
      )}
      <ProductHideOverlay
        isOpen={step === "confirm"}
        onOpenChange={(open) => (open ? undefined : setStep("select"))}
        products={selected}
        onConfirm={(reason, duration) => void confirm(reason, duration)}
        isSubmitting={isSubmitting}
        today={today}
      />
    </>
  );
}
